import { motion } from "framer-motion";
import { Home } from "lucide-react";
import { fadeUp, viewportOnce } from "../../lib/motion";
import { ECOSYSTEM_NODES } from "../../lib/product";
import { SectionIntro } from "./shared";
import { BlueprintGrid } from "../../components/BlueprintGrid";

const CENTER = { x: 50, y: 48 };

const EcosystemDiagram = () => (
    <div
        data-testid="ecosystem-diagram"
        className="relative hidden h-[560px] lg:block"
    >
        <svg
            aria-hidden="true"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            className="absolute inset-0 h-full w-full"
        >
            {ECOSYSTEM_NODES.map((node) => (
                <line
                    key={node.id}
                    x1={CENTER.x}
                    y1={CENTER.y}
                    x2={node.x}
                    y2={node.y}
                    stroke="rgba(255,255,255,0.14)"
                    strokeWidth="1"
                    vectorEffect="non-scaling-stroke"
                />
            ))}
            <line
                x1={CENTER.x}
                y1={0}
                x2={CENTER.x}
                y2={100}
                stroke="rgba(255,255,255,0.05)"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
            />
            <line
                x1={0}
                y1={CENTER.y}
                x2={100}
                y2={CENTER.y}
                stroke="rgba(255,255,255,0.05)"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
            />
        </svg>

        <div
            style={{ left: `${CENTER.x}%`, top: `${CENTER.y}%` }}
            className="absolute z-10 -translate-x-1/2 -translate-y-1/2"
        >
            <div
                data-testid="ecosystem-center"
                className="flex flex-col items-center gap-2 border border-accent/70 bg-[#0C1428] px-8 py-6"
            >
                <Home className="h-6 w-6 text-accent" strokeWidth={1.4} />
                <span className="font-mono text-[13px] tracking-[0.3em] text-white">
                    YOUR HOME
                </span>
                <span className="font-mono text-[13px] tracking-[0.22em] text-cad">
                    0.00, 0.00
                </span>
            </div>
        </div>

        {ECOSYSTEM_NODES.map((node) => (
            <div
                key={node.id}
                style={{ left: `${node.x}%`, top: `${node.y}%` }}
                className="absolute -translate-x-1/2 -translate-y-1/2"
            >
                <div
                    data-testid={`ecosystem-node-${node.slug}`}
                    className={`flex items-center gap-2 border bg-[#0C1428] px-4 py-2.5 font-mono text-[13px] tracking-[0.22em] ${
                        node.tone === "green"
                            ? "border-emerald-400/50 text-emerald-400"
                            : "border-white/20 text-white/85"
                    }`}
                >
                    <span className="text-[13px] text-cad">{node.id}</span>
                    {node.tone === "green" ? (
                        <span className="h-1 w-1 rounded-full bg-emerald-400" />
                    ) : null}
                    {node.label}
                </div>
            </div>
        ))}
    </div>
);

const EcosystemList = () => (
    <ol
        data-testid="ecosystem-list"
        className="relative mt-10 border-l border-white/10 lg:hidden"
    >
        <li className="mb-4 flex items-center gap-3 pl-6">
            <span className="absolute -left-3 flex h-6 w-6 items-center justify-center border border-accent/70 bg-ink">
                <Home className="h-3 w-3 text-accent" />
            </span>
            <span className="font-mono text-[13px] tracking-[0.3em] text-white">
                YOUR HOME
            </span>
        </li>
        {ECOSYSTEM_NODES.map((node, i) => (
            <li
                key={node.id}
                className={`flex items-center gap-4 py-3 pl-6 ${
                    i < ECOSYSTEM_NODES.length - 1
                        ? "border-b border-white/[0.06]"
                        : ""
                }`}
            >
                <span className="font-mono text-[13px] text-cad">
                    {node.id}
                </span>
                <span
                    className={`text-[15px] font-medium ${
                        node.tone === "green"
                            ? "text-emerald-400"
                            : "text-white/90"
                    }`}
                >
                    {node.label}
                </span>
            </li>
        ))}
    </ol>
);

const ProductEcosystem = () => (
    <section
        id="ecosystem"
        data-testid="product-ecosystem-section"
        className="relative overflow-hidden bg-surface/40 py-24 lg:py-32"
    >
        <BlueprintGrid />
        <div className="relative mx-auto max-w-[1600px] px-6 md:px-10 lg:px-16">
            <SectionIntro
                testid="ecosystem-intro"
                align="center"
                eyebrow="PRODUCT ECOSYSTEM"
                title="One project. One source of clarity."
                copy="Every instrument draws on the same verified project foundation — so nothing drifts out of sync."
            />
            <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={viewportOnce}
                className="mt-10"
            >
                <EcosystemDiagram />
                <EcosystemList />
            </motion.div>
        </div>
    </section>
);

export default ProductEcosystem;
